import { Button } from '@kontentino/ui';
import routes, { modalRoutes } from 'constants/routes';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CelebrationModalBase from '../../shared/CelebrationModalBase';
import FIRST_POST_ILLUSTRATION from './icon/first-post-illustration.svg';
import { usePage } from 'modules/page/pageSelector';

type Props = {
  open: boolean;
  onClose: () => void;
  pageId: number;
};

const FirstPostSavedModal: FC<Props> = ({ open, onClose, pageId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pages } = usePage();

  const isPageConnected = useMemo(() => {
    const page = pages.find((p) => p.id === pageId);
    return page?.isConnected ?? false;
  }, [pages, pageId]);

  return (
    <CelebrationModalBase
      open={open}
      onClose={onClose}
      dataName="first-post-saved-modal"
      dataCy="first-post-saved-modal"
      image={
        <div className="tw-flex tw-min-w-[222px] tw-justify-center">
          <img src={FIRST_POST_ILLUSTRATION} alt="Your first post is saved" />
        </div>
      }
      title={
        isPageConnected
          ? t('firstPostSavedModal.titlePageConnected', {
              defaultValue: `Congrats on finishing your first post! 🎉`,
            })
          : t('firstPostSavedModal.titlePageNotConnected', {
              defaultValue: `High five! Your first post is saved. 🙌`,
            })
      }
      description={
        isPageConnected
          ? t('firstPostSavedModal.descriptionPageConnected', {
              defaultValue:
                'Want to speed things up? Kontentino AI helps you create on-brand content, generate post ideas, and write copy that sounds like you.',
            })
          : t('firstPostSavedModal.descriptionPageNotConnected', {
              defaultValue:
                'Ready to see it live? Connect your profile to publish and keep the momentum going.',
            })
      }
      button={
        <Button
          className="tw-w-full"
          onClick={() =>
            navigate(
              isPageConnected
                ? {
                    pathname: routes.BRAND_HUB,
                    hash: modalRoutes.BRAND_HUB.MANAGE_PROFILE,
                  }
                : { hash: modalRoutes.NEW_PROFILE },
            )
          }
          variant="primary"
          size="large"
          data-name={`first-post-saved_${
            isPageConnected
              ? 'manage-brand-profile-button'
              : 'connect-profile-button'
          }`}
          data-cy={`first-post-saved_${
            isPageConnected
              ? 'manage-brand-profile-button'
              : 'connect-profile-button'
          }`}
        >
          {isPageConnected
            ? t('setupYourBrandProfileCta', {
                defaultValue: 'Set up your brand profile',
              })
            : t('connectYourProfileCta', {
                defaultValue: 'Connect your profile',
              })}
        </Button>
      }
    />
  );
};

export default FirstPostSavedModal;
